import { ILocation } from '@rbi-ctg/frontend';

import { squared } from '../calc';

const EARTH_RADIUS = 6378137; // radius in meters - value used by google maps
// see: https://gis.stackexchange.com/questions/7430/what-ratio-scales-do-google-maps-zoom-levels-correspond-to

export function toRadians(degrees: number) {
  return (degrees * Math.PI) / 180;
}

export function toDegrees(radians: number) {
  return (radians * 180) / Math.PI;
}

function isLocation(point: Partial<ILocation>): point is ILocation {
  return !!point && !!point.lat && !!point.lng;
}

/**
 * given 2 points, calculate the great sphere
 * distance between them
 */
export function haversine(point1: Partial<ILocation>, point2: Partial<ILocation>): number {
  if (!isLocation(point1) || !isLocation(point2)) {
    return 0;
  }

  const lat1 = toRadians(point1.lat);
  const lat2 = toRadians(point2.lat);
  const latDelta = toRadians(point2.lat - point1.lat);
  const lngDelta = toRadians(point2.lng - point1.lng);

  const a =
    Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(lngDelta / 2) * Math.sin(lngDelta / 2);
  const circumference = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distanceInMeters = EARTH_RADIUS * circumference;
  const distanceInMiles = distanceInMeters / 1609.34;
  return distanceInMiles;
}

/**
 * given a starting point lat/lng, a bearing in degrees
 * and a distance, calculates the resulting lat/lng
 */
export function reverseHaversine(
  lat: number,
  lng: number,
  bearing: number,
  distance: number
): ILocation {
  const lat1 = toRadians(lat);
  const lng1 = toRadians(lng);
  const angularDistance = distance / EARTH_RADIUS;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing)
  );

  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
      Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
    );

  return { lat: toDegrees(lat2), lng: toDegrees(lng2) };
}

export function metersPerPx(lat: number, zoom: number): number {
  // see: https://gis.stackexchange.com/questions/7430/what-ratio-scales-do-google-maps-zoom-levels-correspond-to
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
}

interface ICorners {
  ne: ILocation;
  sw: ILocation;
}

/**
 * derive the corners given the center of a google map, the zoom level
 * and the diagonal of the map in px
 * @param {number} lat the latitude of the center of the map
 * @param {number} lng the longitude of the center of the map
 * @param {number} zoom the zoomLevel of the map
 *                      - return value of map.getZoom()
 * @param {number} distanceInPx the diagonal length of the map
 *
 * @return {ICorners} the corners of the map
 */
export function cornersForPoint(
  lat: number,
  lng: number,
  zoom: number,
  distanceInPx: number
): ICorners {
  const centerToCorner = metersPerPx(lat, zoom) * (distanceInPx / 2);
  const ne = reverseHaversine(lat, lng, toRadians(45), centerToCorner);
  const sw = reverseHaversine(lat, lng, toRadians(225), centerToCorner);
  return { ne, sw };
}

/**
 * derive the corners given a lat & lng, the zoom level
 * and the diameter in meters
 * @param {number} lat the latitude of the center of the map
 * @param {number} lng the longitude of the center of the map
 * @param {number} diagonal the diagonal distance in meters from corner to corner
 *
 * @return {ICorners} the corners of the map
 */
export function cornersForPointWithDiagonal(lat: number, lng: number, diagonal: number): ICorners {
  const centerToCorner = diagonal / 2;
  const ne = reverseHaversine(lat, lng, toRadians(45), centerToCorner);
  const sw = reverseHaversine(lat, lng, toRadians(225), centerToCorner);
  return { ne, sw };
}

export function hypotenuse(a: number, b: number) {
  const cSquared = squared(a) + squared(b);
  return Math.pow(cSquared, 0.5);
}
